import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Send, ShoppingCart, AlertTriangle, Info } from "lucide-react";
import SEO from "@/components/SEO";
import InstrumentSVG from "@/components/InstrumentSVG";
import InquiryDialog from "@/components/InquiryDialog";
import publicApi from "@/lib/publicApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { parseLocaleFromPath, localePath } from "@/lib/locale";
import { formatEurPlain } from "@/lib/money";
import { BreadcrumbLD } from "@/components/StructuredData";

const SLUG_TO_PRODUCT_ID = {
  bisernice: "bisernica",
  bracevi: "brac",
  bugarije: "bugarija",
  cela: "celo",
  berde: "berda",
  mandoline: "mandolina",
};

const L = {
  hr: { title: "Konfigurator", subtitle: "Odaberite drvo, ukrase i završnu obradu — cijena se ažurira u stvarnom vremenu.", base: "Osnovna cijena", total: "Ukupno", required: "obavezno", help: "info", build_cta: "Pošalji upit za izradu", cart_cta: "Dodaj u košaricu", back: "Natrag", config_summary: "Vaš izbor", price_breakdown: "Razrada cijene", added: "Dodano u košaricu", inquiry_title: "Upit za izradu", inquiry_subtitle: "Pošaljite nam svoje detalje i javit ćemo Vam se s ponudom.", inquiry_name: "Ime i prezime", inquiry_email: "E-mail", inquiry_phone: "Telefon", inquiry_prefer: "Preferirani način kontakta", inquiry_prefer_email: "E-mail", inquiry_prefer_phone: "Telefon", inquiry_message: "Poruka (opcionalno)", inquiry_submit: "Pošalji upit", inquiry_sending: "Slanje…", inquiry_success_title: "Hvala — upit zaprimljen.", inquiry_success_body: "Pregledat ćemo Vaš upit i javiti se u roku od 1-2 radna dana.", inquiry_error: "Slanje nije uspjelo. Pokušajte ponovo.", inquiry_cancel: "Odustani", inquiry_close: "Zatvori", inquiry_summary: "Vaša konfiguracija" },
  en: { title: "Configurator", subtitle: "Choose woods, inlays and finish — price updates live.", base: "Base price", total: "Total", required: "required", help: "info", build_cta: "Send build enquiry", cart_cta: "Add to cart", back: "Back", config_summary: "Your selection", price_breakdown: "Price breakdown", added: "Added to cart", inquiry_title: "Build enquiry", inquiry_subtitle: "Send us your details and we'll respond with a quote.", inquiry_name: "Full name", inquiry_email: "Email", inquiry_phone: "Phone", inquiry_prefer: "Preferred contact", inquiry_prefer_email: "Email", inquiry_prefer_phone: "Phone", inquiry_message: "Message (optional)", inquiry_submit: "Send enquiry", inquiry_sending: "Sending…", inquiry_success_title: "Thank you — enquiry received.", inquiry_success_body: "We'll review your enquiry and respond within 1-2 business days.", inquiry_error: "Submission failed. Please try again.", inquiry_cancel: "Cancel", inquiry_close: "Close", inquiry_summary: "Your configuration" },
  de: { title: "Konfigurator", subtitle: "Wählen Sie Hölzer, Einlagen und Oberfläche — der Preis aktualisiert sich live.", base: "Grundpreis", total: "Gesamt", required: "Pflicht", help: "Info", build_cta: "Bauanfrage senden", cart_cta: "In den Warenkorb", back: "Zurück", config_summary: "Ihre Auswahl", price_breakdown: "Preisaufschlüsselung", added: "Zum Warenkorb hinzugefügt", inquiry_title: "Bauanfrage", inquiry_subtitle: "Senden Sie uns Ihre Details und wir melden uns mit einem Angebot.", inquiry_name: "Vollständiger Name", inquiry_email: "E-Mail", inquiry_phone: "Telefon", inquiry_prefer: "Bevorzugter Kontakt", inquiry_prefer_email: "E-Mail", inquiry_prefer_phone: "Telefon", inquiry_message: "Nachricht (optional)", inquiry_submit: "Anfrage senden", inquiry_sending: "Senden…", inquiry_success_title: "Danke — Anfrage erhalten.", inquiry_success_body: "Wir prüfen Ihre Anfrage und antworten innerhalb von 1-2 Werktagen.", inquiry_error: "Übermittlung fehlgeschlagen. Bitte erneut versuchen.", inquiry_cancel: "Abbrechen", inquiry_close: "Schließen", inquiry_summary: "Ihre Konfiguration" },
  fr: { title: "Configurateur", subtitle: "Choisissez bois, marqueteries et finition — le prix s'actualise en direct.", base: "Prix de base", total: "Total", required: "requis", help: "info", build_cta: "Envoyer une demande", cart_cta: "Ajouter au panier", back: "Retour", config_summary: "Votre sélection", price_breakdown: "Détail du prix", added: "Ajouté au panier", inquiry_title: "Demande de fabrication", inquiry_subtitle: "Envoyez-nous vos coordonnées et nous reviendrons avec un devis.", inquiry_name: "Nom complet", inquiry_email: "E-mail", inquiry_phone: "Téléphone", inquiry_prefer: "Contact préféré", inquiry_prefer_email: "E-mail", inquiry_prefer_phone: "Téléphone", inquiry_message: "Message (facultatif)", inquiry_submit: "Envoyer la demande", inquiry_sending: "Envoi…", inquiry_success_title: "Merci — demande reçue.", inquiry_success_body: "Nous examinerons votre demande et répondrons sous 1-2 jours ouvrables.", inquiry_error: "Échec de l'envoi. Veuillez réessayer.", inquiry_cancel: "Annuler", inquiry_close: "Fermer", inquiry_summary: "Votre configuration" },
};

function findOption(field, value) {
  return (field.options || []).find((o) => o.id === value);
}

export default function Configure() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { locale } = parseLocaleFromPath(location.pathname);
  const t = L[locale] || L.hr;
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState("");
  const [values, setValues] = useState({}); // {field_id: value}
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [errors, setErrors] = useState({}); // field_id → error string

  const productId = SLUG_TO_PRODUCT_ID[slug] || slug;

  useEffect(() => {
    publicApi.get(`/public/product-types/${slug}`, { params: { locale } })
      .then((r) => {
        setProduct(r.data);
        // Init defaults
        const defaults = {};
        (r.data.customization_fields || []).forEach((f) => {
          defaults[f.id] = f.default_value !== undefined ? f.default_value : (f.type === "select" ? (f.options?.[0]?.id || "") : (f.type === "checkbox" ? false : ""));
        });
        setValues(defaults);
      })
      .catch((err) => setError(err?.response?.data?.detail || "Could not load product"));
  }, [slug, locale]);

  // Compute price + price breakdown + configuration list
  const { totalPrice, breakdown, configurationList } = useMemo(() => {
    if (!product) return { totalPrice: 0, breakdown: [], configurationList: [] };
    const base = Number(product.base_price_eur || 0);
    const lines = [];
    let sum = base;
    (product.customization_fields || []).forEach((f) => {
      const v = values[f.id];
      let delta = 0;
      let valueLabel = "";
      if (f.type === "select") {
        const opt = findOption(f, v);
        if (opt) {
          delta = Number(opt.price_delta_eur || 0);
          valueLabel = (opt.label && opt.label[locale]) || (opt.label && opt.label.hr) || opt.id;
        }
      } else if (f.type === "checkbox" && v) {
        delta = Number(f.price_delta_eur_if_true || 0);
        valueLabel = "✓";
      } else if (f.type === "text" && v && v.trim()) {
        delta = Number(f.price_delta_eur_if_filled || 0);
        valueLabel = v.trim();
      } else if (f.type === "number" && v !== "") {
        valueLabel = String(v);
      }
      if (valueLabel) {
        lines.push({
          field_id: f.id,
          field_label: (f.label && f.label[locale]) || (f.label && f.label.hr) || f.id,
          value: v,
          value_label: valueLabel,
          price_delta_eur: delta,
        });
        sum += delta;
      }
    });
    return { totalPrice: sum, breakdown: lines, configurationList: lines };
  }, [product, values, locale]);

  // SVG preview config — derive hexes + finish + inlay from selected options
  const previewConfig = useMemo(() => {
    if (!product) return {};
    const fields = product.customization_fields || [];
    const woodTopField = fields.find((f) => f.id === "wood_top");
    const woodBackField = fields.find((f) => f.id === "wood_back");
    const inlay = values.inlay;
    const finish = values.finish;
    const top = woodTopField && findOption(woodTopField, values.wood_top);
    const back = woodBackField && findOption(woodBackField, values.wood_back);
    return {
      wood_top_hex: top?.hex,
      wood_back_hex: back?.hex,
      inlay,
      finish,
    };
  }, [product, values]);

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-20 text-center">
        <AlertTriangle className="h-10 w-10 text-destructive mx-auto mb-4" strokeWidth={1.3} />
        <h1 className="font-serif text-3xl text-foreground">{error}</h1>
      </div>
    );
  }
  if (!product) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-20 text-center text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mx-auto" /> Loading…
      </div>
    );
  }

  const validateRequired = () => {
    const errs = {};
    (product.customization_fields || []).forEach((f) => {
      if (f.required) {
        const v = values[f.id];
        if (f.type === "select" && (!v || v === "")) errs[f.id] = "Required";
        if (f.type === "checkbox" && !v) {/* don't enforce */}
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onPrimary = () => {
    if (!validateRequired()) {
      toast.error("Please complete all required fields.");
      return;
    }
    if (product.mode === "ready_stock") {
      addItem({
        product_slug: product.id,
        product_name: product.name,
        unit_price_eur: totalPrice,
        configuration: configurationList,
        preview_config: previewConfig,
        engraving_text: values.engraving_text || "",
      });
      toast.success(t.added);
    } else {
      setInquiryOpen(true);
    }
  };

  const isReadyStock = product.mode === "ready_stock";
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <>
      <SEO title={`${product.name} — ${t.title} | Tambure Žmegač`} description={product.tagline} />
      <BreadcrumbLD
        origin={origin}
        items={[
          { name: t.back, url: localePath(locale, "/models") },
          { name: product.name, url: localePath(locale, `/${slug}`) },
          { name: t.title, url: location.pathname },
        ]}
      />
      <section className="bg-background grain-bg">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-12 lg:py-16">
          <Link to={localePath(locale, `/${slug}`)} data-testid="configure-back" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6">
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> {t.back}
          </Link>
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.3em] text-primary mb-3">{t.title}</div>
            <h1 className="font-serif text-5xl lg:text-6xl text-foreground leading-[1.05]">{product.name}</h1>
            <p className="mt-3 text-base text-foreground/75">{t.subtitle}</p>
          </div>

          <div className="mt-10 grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
            {/* PREVIEW (sticky on desktop) */}
            <div className="lg:sticky lg:top-24">
              <div className="rounded-lg bg-tz-espresso/95 border border-border/60 shadow-ambient overflow-hidden">
                <div className="aspect-[4/5] bg-gradient-to-b from-tz-espresso to-[#2A1810] p-6 flex items-center justify-center" data-testid="configure-preview">
                  <InstrumentSVG type={product.id} config={previewConfig} engravingText={values.engraving_text || ""} />
                </div>
                <div className="p-5 border-t border-white/5 text-tz-cream">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-tz-cream/60">{t.base}</span>
                    <span className="text-sm text-tz-cream/85">{formatEurPlain(product.base_price_eur)}</span>
                  </div>
                  <ul className="mt-3 space-y-1.5 text-[11px] text-tz-cream/65 max-h-32 overflow-y-auto pr-1" data-testid="configure-breakdown">
                    {breakdown.filter((b) => b.price_delta_eur > 0).map((b) => (
                      <li key={b.field_id} className="flex items-baseline justify-between gap-2">
                        <span>{b.field_label}: <span className="text-tz-cream/85">{b.value_label}</span></span>
                        <span className="whitespace-nowrap">+{formatEurPlain(b.price_delta_eur)}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 pt-4 border-t border-white/5 flex items-baseline justify-between" data-testid="configure-total">
                    <span className="text-sm uppercase tracking-[0.18em] text-tz-cream/60">{t.total}</span>
                    <span className="font-serif text-3xl text-primary">{formatEurPlain(totalPrice)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* FORM */}
            <div className="space-y-6">
              {(product.customization_fields || []).map((f) => {
                const labelText = (f.label && f.label[locale]) || (f.label && f.label.hr) || f.id;
                const helpText = (f.help && f.help[locale]) || (f.help && f.help.hr) || "";
                const v = values[f.id];
                const err = errors[f.id];

                if (f.type === "select") {
                  return (
                    <div key={f.id} className="bg-card rounded-lg border border-border/60 shadow-ambient p-5" data-testid={`configure-field-${f.id}`}>
                      <div className="flex items-baseline justify-between gap-2">
                        <Label className="text-sm font-medium text-foreground">
                          {labelText} {f.required && <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground ml-1">{t.required}</span>}
                        </Label>
                      </div>
                      {helpText && <p className="mt-1 text-xs text-muted-foreground flex items-start gap-1.5"><Info className="h-3 w-3 mt-0.5 shrink-0" strokeWidth={1.6} /> {helpText}</p>}
                      <div className="mt-3 grid sm:grid-cols-2 gap-2">
                        {(f.options || []).map((opt) => {
                          const isSel = v === opt.id;
                          const optLabel = (opt.label && opt.label[locale]) || (opt.label && opt.label.hr) || opt.id;
                          return (
                            <button
                              key={opt.id}
                              type="button"
                              onClick={() => setValues((s) => ({ ...s, [f.id]: opt.id }))}
                              data-testid={`configure-option-${f.id}-${opt.id}`}
                              className={`group text-left rounded-lg border p-3 transition-colors ${
                                isSel ? "border-primary bg-primary/8" : "border-border/60 bg-background hover:border-primary/40"
                              }`}
                            >
                              <div className="flex items-center gap-2.5">
                                {opt.hex && (
                                  <span className="h-5 w-5 rounded-md border border-border/60 shrink-0" style={{ backgroundColor: opt.hex }} />
                                )}
                                <span className="flex-1 text-sm text-foreground/90">{optLabel}</span>
                                {opt.price_delta_eur > 0 && (
                                  <span className="text-[11px] text-muted-foreground whitespace-nowrap">+{formatEurPlain(opt.price_delta_eur)}</span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      {err && <div className="mt-2 text-xs text-destructive">{err}</div>}
                    </div>
                  );
                }
                if (f.type === "text") {
                  return (
                    <div key={f.id} className="bg-card rounded-lg border border-border/60 shadow-ambient p-5" data-testid={`configure-field-${f.id}`}>
                      <Label className="text-sm font-medium text-foreground">{labelText}</Label>
                      {helpText && <p className="mt-1 text-xs text-muted-foreground">{helpText}</p>}
                      <Input
                        value={v || ""}
                        maxLength={f.max_length || 60}
                        onChange={(e) => setValues((s) => ({ ...s, [f.id]: e.target.value }))}
                        className="mt-3 h-10 bg-background"
                        data-testid={`configure-text-${f.id}`}
                      />
                      <div className="mt-1 text-[11px] text-muted-foreground">{(v || "").length}/{f.max_length || 60}</div>
                    </div>
                  );
                }
                if (f.type === "checkbox") {
                  return (
                    <label key={f.id} className="flex items-start gap-3 bg-card rounded-lg border border-border/60 shadow-ambient p-5 cursor-pointer" data-testid={`configure-field-${f.id}`}>
                      <Checkbox
                        checked={!!v}
                        onCheckedChange={(c) => setValues((s) => ({ ...s, [f.id]: !!c }))}
                        data-testid={`configure-checkbox-${f.id}`}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="text-sm font-medium text-foreground">{labelText}</span>
                          {f.price_delta_eur_if_true && <span className="text-[11px] text-muted-foreground whitespace-nowrap">+{formatEurPlain(f.price_delta_eur_if_true)}</span>}
                        </div>
                        {helpText && <p className="mt-1 text-xs text-muted-foreground">{helpText}</p>}
                      </div>
                    </label>
                  );
                }
                return null;
              })}

              {/* CTA */}
              <div className="bg-tz-espresso text-tz-cream rounded-lg p-5 flex items-center justify-between gap-4 shadow-ambient" data-testid="configure-cta-bar">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-tz-cream/60">{t.total}</div>
                  <div className="font-serif text-3xl text-primary mt-0.5">{formatEurPlain(totalPrice)}</div>
                </div>
                <Button
                  data-testid="configure-primary-cta"
                  onClick={onPrimary}
                  className="h-12 px-5 shadow-interactive"
                >
                  {isReadyStock ? <ShoppingCart className="h-4 w-4" strokeWidth={1.5} /> : <Send className="h-4 w-4" strokeWidth={1.5} />}
                  {isReadyStock ? t.cart_cta : t.build_cta}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <InquiryDialog
        open={inquiryOpen}
        onOpenChange={setInquiryOpen}
        product={{ id: product.id, name: product.name, base_price_eur: product.base_price_eur }}
        configuration={configurationList}
        totalPriceEur={totalPrice}
        locale={locale}
        labels={{
          title: t.inquiry_title,
          subtitle: t.inquiry_subtitle,
          name: t.inquiry_name,
          email: t.inquiry_email,
          phone: t.inquiry_phone,
          prefer: t.inquiry_prefer,
          prefer_email: t.inquiry_prefer_email,
          prefer_phone: t.inquiry_prefer_phone,
          message: t.inquiry_message,
          submit: t.inquiry_submit,
          sending: t.inquiry_sending,
          success_title: t.inquiry_success_title,
          success_body: t.inquiry_success_body,
          error: t.inquiry_error,
          summary: t.inquiry_summary,
          total: t.total,
          cancel: t.inquiry_cancel,
          close: t.inquiry_close,
        }}
      />
    </>
  );
}
